import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
    Briefcase,
    Eye,
    FileText,
    MapPin,
    Megaphone,
    Send,
    Settings2,
    Sparkles,
    X,
} from "lucide-react";
import Swal from "sweetalert2";
import "./Annoncemodal.css";

import {
    clearRecrutementEmailState,
    sendOfferToChasseursRequest,
    sendOfferToPartenairesRequest,
} from "../../../../redux/actions/demandesRecrutementActions";
import { fetchChasseursRequest } from "../../../../redux/actions/chasseursActions";
import {
    clearGeneratedOffreEmploi,
    generateOffreEmploiRequest,
    publishOffreEmploiCareersRequest,
    saveOffreEmploiDraftRequest,
} from "../../../../redux/actions/offresEmploiActions";
import { fetchPartenairesAcadRequest } from "../../../../redux/actions/partenairesAcademiquesActions";

const emptyDraft = {
    titre: "",
    lieu: "",
    langue: "fr",
    slug: "",
    contenuFinal: "",
};

const showSwal = (options) => {
    Swal.fire({
        ...options,
        customClass: {
            ...(options.customClass || {}),
            container: "swal-above-modal",
        },
        didOpen: () => {
            const container = document.querySelector(".swal-above-modal");
            if (container) container.style.zIndex = "99999";
            if (options.didOpen) options.didOpen();
        },
    });
};

function AnnonceModal({
    open,
    onClose,
    demande,
    loading,
    saving,
    error,
    generatedOffer,
    chasseurs,
    partenairesAcademiques,
    sendingOfferToChasseurs,
    sendingOfferToPartenaires,
    sendOfferSuccess,
    sendOfferError,
    generateOffreEmploi,
    saveOffreEmploiDraft,
    publishOffreEmploiCareers,
    clearGeneratedOffreEmploi,
    fetchChasseurs,
    fetchPartenaires,
    sendOfferToChasseurs,
    sendOfferToPartenaires,
    clearRecrutementEmailState,
}) {
    const [activeTab, setActiveTab] = useState(0);
    const [language, setLanguage] = useState("fr");
    const [draft, setDraft] = useState(emptyDraft);
    const [selectedChasseurs, setSelectedChasseurs] = useState([]);
    const [selectedPartenaires, setSelectedPartenaires] = useState([]);
    const [dateLimiteOffre, setDateLimiteOffre] = useState("");
    const [dateEnvoiEmail, setDateEnvoiEmail] = useState(new Date().toISOString().slice(0, 10));

    const safeDemande = demande || {};

    useEffect(() => {
        if (!open) {
            document.body.style.overflow = "unset";
            setActiveTab(0);
            setLanguage("fr");
            setDraft(emptyDraft);
            setSelectedChasseurs([]);
            setSelectedPartenaires([]);
            setDateLimiteOffre("");
            setDateEnvoiEmail(new Date().toISOString().slice(0, 10));
            clearGeneratedOffreEmploi();
            clearRecrutementEmailState();
            return;
        }

        document.body.style.overflow = "hidden";
        fetchChasseurs();
        fetchPartenaires();

        return () => {
            document.body.style.overflow = "unset";
        };
    }, [open, clearGeneratedOffreEmploi, clearRecrutementEmailState, fetchChasseurs, fetchPartenaires]);

    useEffect(() => {
        if (sendOfferSuccess) {
            showSwal({
                icon: "success",
                text: sendOfferSuccess,
                timer: 2200,
                showConfirmButton: false,
            });

            setSelectedChasseurs([]);
            setSelectedPartenaires([]);
            clearRecrutementEmailState();
        }
    }, [sendOfferSuccess, clearRecrutementEmailState]);

    useEffect(() => {
        if (sendOfferError) {
            showSwal({
                icon: "error",
                text: sendOfferError,
                timer: 2800,
                showConfirmButton: false,
            });

            const timer = setTimeout(() => {
                clearRecrutementEmailState();
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [sendOfferError, clearRecrutementEmailState]);

    useEffect(() => {
        if (!generatedOffer) return;

        setDraft({
            titre: generatedOffer.titre || "",
            lieu: generatedOffer.lieu || "",
            langue: generatedOffer.langue || language,
            slug: generatedOffer.slug || "",
            contenuFinal: generatedOffer.contenuFinal || generatedOffer.contenuGenere || "",
        });
        setLanguage(generatedOffer.langue || language);
    }, [generatedOffer]);

    const displayedPoste = useMemo(
        () => safeDemande.autrePoste || safeDemande.posteNom || safeDemande.poste || "—",
        [safeDemande]
    );

    const displayedEtablissement = useMemo(
        () =>
            safeDemande.autreEtablissement ||
            safeDemande.etablissementNom ||
            safeDemande.etabliseement ||
            "—",
        [safeDemande]
    );

    // const statusLabel = generatedOffer?.statutPublication || "BROUILLON";

    const toggleChasseur = (id) => {
        setSelectedChasseurs((prev) =>
            prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
        );
    };

    const togglePartenaire = (id) => {
        setSelectedPartenaires((prev) =>
            prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
        );
    };

    const handleGenerate = () => {
        if (!safeDemande?.id) return;

        generateOffreEmploi({
            demandeRecrutementId: safeDemande.id,
            language,
        });
    };

    const handleSaveDraft = () => {
        if (!generatedOffer?.id) return;

        saveOffreEmploiDraft(generatedOffer.id, {
            titre: draft.titre,
            lieu: draft.lieu,
            contenuFinal: draft.contenuFinal,
        });
    };

    const handlePublishCareers = () => {
        publishOffreEmploiCareers({
            offreEmploiId: generatedOffer?.id,
            demandeRecrutementId: safeDemande?.id,
        });
    };

    const handleSendToChasseurs = () => {
        if (selectedChasseurs.length === 0) {
            showSwal({
                icon: "warning",
                text: "Veuillez sélectionner au moins un chasseur de tête",
                timer: 2200,
                showConfirmButton: false,
            });
            return;
        }

        if (!dateLimiteOffre) {
            showSwal({
                icon: "warning",
                text: "Veuillez renseigner la date limite de l'offre",
                timer: 2200,
                showConfirmButton: false,
            });
            return;
        }

        if (!safeDemande?.id) {
            showSwal({
                icon: "error",
                text: "Demande de recrutement introuvable",
                timer: 2200,
                showConfirmButton: false,
            });
            return;
        }

        sendOfferToChasseurs({
            demandeId: safeDemande.id,
            chasseurIds: selectedChasseurs,
            dateLimiteOffre,
            dateEnvoiEmail,
        });
    };

    const handleSendToPartenaires = () => {
        if (selectedPartenaires.length === 0) {
            showSwal({
                icon: "warning",
                text: "Veuillez sélectionner au moins un partenaire académique",
                timer: 2200,
                showConfirmButton: false,
            });
            return;
        }

        if (!dateLimiteOffre) {
            showSwal({
                icon: "warning",
                text: "Veuillez renseigner la date limite de l'offre",
                timer: 2200,
                showConfirmButton: false,
            });
            return;
        }

        if (!safeDemande?.id) {
            showSwal({
                icon: "error",
                text: "Demande de recrutement introuvable",
                timer: 2200,
                showConfirmButton: false,
            });
            return;
        }

        sendOfferToPartenaires({
            demandeId: safeDemande.id,
            partenaireIds: selectedPartenaires,
            dateLimiteOffre,
            dateEnvoiEmail,
        });
    };

    const handleClose = () => {
        clearGeneratedOffreEmploi();
        clearRecrutementEmailState();
        onClose();
    };

    const isSaveDisabled =
        !generatedOffer?.id ||
        saving ||
        !draft.titre.trim() ||
        !draft.lieu.trim() ||
        !draft.contenuFinal.trim();

    if (!open) return null;

    return (
        <div className="anModal" onMouseDown={handleClose} role="dialog" aria-modal="true">
            <div className="anModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="anModal__header">
                    <div className="anModal__title">
                        <Megaphone size={18} />
                        Annonce — {displayedPoste}
                    </div>
                    <button className="anIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={18} />
                    </button>
                </div>

                <div className="anTabs">
                    <button
                        className={`anTab ${activeTab === 0 ? "anTab--active" : ""}`}
                        onClick={() => setActiveTab(0)}
                        type="button"
                    >
                        <Megaphone size={14} />
                        Offre
                    </button>
                    <button
                        className={`anTab ${activeTab === 1 ? "anTab--active" : ""}`}
                        onClick={() => setActiveTab(1)}
                        type="button"
                    >
                        <Send size={14} />
                        Chasseurs de tête
                    </button>
                    <button
                        className={`anTab ${activeTab === 2 ? "anTab--active" : ""}`}
                        onClick={() => setActiveTab(2)}
                        type="button"
                    >
                        <Send size={14} />
                        Partenaires académiques
                    </button>
                </div>

                <div className="anModal__body">
                    {activeTab === 0 && (
                        <div className="anContent">
                            <div className="anCard">
                                <div className="anCardHeader">
                                    <h3 className="anCardTitle">Données source de la demande</h3>
                                </div>

                                <div className="anInfoGrid">
                                    <div className="anInfoItem">
                                        <span className="anInfoLabel">Poste</span>
                                        <span className="anInfoValue">{displayedPoste}</span>
                                    </div>
                                    <div className="anInfoItem">
                                        <span className="anInfoLabel">Type d'emploi</span>
                                        <span className="anInfoValue">{safeDemande.typeEmploi || "—"}</span>
                                    </div>
                                    <div className="anInfoItem">
                                        <span className="anInfoLabel">Niveau d'études</span>
                                        <span className="anInfoValue">
                                            {safeDemande.niveauEtude || safeDemande.niveauxEtudes || "—"}
                                        </span>
                                    </div>
                                    <div className="anInfoItem">
                                        <span className="anInfoLabel">Expérience</span>
                                        <span className="anInfoValue">{safeDemande.anneesExperience || "—"}</span>
                                    </div>
                                    <div className="anInfoItem">
                                        <span className="anInfoLabel">Lieu source</span>
                                        <span className="anInfoValue">{displayedEtablissement}</span>
                                    </div>
                                    <div className="anInfoItem anInfoItem--wide">
                                        <span className="anInfoLabel">Profil recherché</span>
                                        <span className="anInfoValue">
                                            {safeDemande.profilRecherche || safeDemande.profileRecherche || "—"}
                                        </span>
                                    </div>
                                    <div className="anInfoItem anInfoItem--wide">
                                        <span className="anInfoLabel">Description complémentaire</span>
                                        <span className="anInfoValue">{safeDemande.description || "—"}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="anCard">
                                <div className="anCardHeader">
                                    <h3 className="anCardTitle">Paramètres de génération</h3>
                                </div>

                                <div className="anOfferToolbar">
                                    <div className="anField anField--small">
                                        <label>Langue de génération</label>
                                        <select
                                            value={language}
                                            onChange={(e) => setLanguage(e.target.value)}
                                            disabled={loading}
                                        >
                                            <option value="fr">Français</option>
                                            <option value="en">English</option>
                                        </select>
                                    </div>

                                    <div className="anGenerateBox">
                                        <div className="anGenerateBox__text">
                                            La génération IA enrichit l'offre à partir des vraies données de la demande et préremplit le contenu final éditable.
                                        </div>
                                        <button
                                            className="anBtn anBtn--primary"
                                            onClick={handleGenerate}
                                            type="button"
                                            disabled={loading || !safeDemande?.id}
                                        >
                                            <Sparkles size={16} />
                                            {loading ? "Génération en cours..." : "Générer l'offre avec IA"}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="anCard">
                                <div className="anCardHeader">
                                    <h3 className="anCardTitle">Offre d'emploi</h3>
                                </div>

                                <div className="anOfferGrid">
                                    <div className="anField">
                                        <label>Titre</label>
                                        <div className="anFieldIconWrap">
                                            <Briefcase size={15} />
                                            <input
                                                type="text"
                                                value={draft.titre}
                                                onChange={(e) =>
                                                    setDraft((prev) => ({ ...prev, titre: e.target.value }))
                                                }
                                                placeholder="Titre de l'offre"
                                            />
                                        </div>
                                    </div>

                                    <div className="anField">
                                        <label>Lieu</label>
                                        <div className="anFieldIconWrap">
                                            <MapPin size={15} />
                                            <input
                                                type="text"
                                                value={draft.lieu}
                                                onChange={(e) =>
                                                    setDraft((prev) => ({ ...prev, lieu: e.target.value }))
                                                }
                                                placeholder="Lieu"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/*<div className="anMetaGrid">*/}
                                {/*    <div className="anMetaCard">*/}
                                {/*        <span className="anInfoLabel">Statut</span>*/}
                                {/*        <span className="anInfoValue">{statusLabel}</span>*/}
                                {/*    </div>*/}
                                {/*</div>*/}

                                <div className="anField">
                                    <label>Contenu final</label>
                                    <textarea
                                        className="anTextarea anTextarea--offer"
                                        rows={16}
                                        value={draft.contenuFinal}
                                        onChange={(e) =>
                                            setDraft((prev) => ({ ...prev, contenuFinal: e.target.value }))
                                        }
                                        placeholder="Le contenu généré apparaîtra ici et restera modifiable."
                                    />
                                </div>

                                {error && <div className="anAlert anAlert--error">{error}</div>}

                                <div className="anOfferActions">
                                    <button className="anBtn anBtn--ghost" type="button" onClick={handleClose}>
                                        Annuler
                                    </button>
                                    {/*<button*/}
                                    {/*    className="anBtn anBtn--secondary"*/}
                                    {/*    type="button"*/}
                                    {/*    onClick={handleSaveDraft}*/}
                                    {/*    disabled={isSaveDisabled}*/}
                                    {/*>*/}
                                    {/*    <FileText size={15} />*/}
                                    {/*    {saving ? "Enregistrement..." : "Enregistrer brouillon"}*/}
                                    {/*</button>*/}
                                    <button
                                        className="anBtn anBtn--publish"
                                        type="button"
                                        onClick={handlePublishCareers}
                                        disabled={!generatedOffer?.id || loading || saving}
                                    >
                                        <Eye size={16} />
                                        Publier sur Careers
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 1 && (
                        <div className="anContent">
                            <div className="anCard">
                                <div className="anCardHeader">
                                    <h3 className="anCardTitle">
                                        Envoyer l'annonce aux chasseurs de tête ({selectedChasseurs.length} sélectionné(s))
                                    </h3>
                                </div>

                                <div className="anTableWrap">
                                    <table className="anTable">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 50 }}>
                                                    <input type="checkbox" disabled />
                                                </th>
                                                <th>Nom du chasseur</th>
                                                <th>Email</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(chasseurs || []).map((chasseur) => {
                                                const isSelected = selectedChasseurs.includes(chasseur.id);
                                                return (
                                                    <tr key={chasseur.id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleChasseur(chasseur.id)}
                                                            />
                                                        </td>
                                                        <td className="anStrong">{chasseur.nom}</td>
                                                        <td className="anMuted">{chasseur.email}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="anDateSection">
                                    <div className="anField">
                                        <label>Date limite de l'offre</label>
                                        <input
                                            type="date"
                                            value={dateLimiteOffre}
                                            onChange={(e) => setDateLimiteOffre(e.target.value)}
                                        />
                                    </div>

                                    <div className="anField">
                                        <label>Date d'envoi de l'email</label>
                                        <input
                                            type="date"
                                            value={dateEnvoiEmail}
                                            onChange={(e) => setDateEnvoiEmail(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="anTextRight">
                                    <button
                                        className="anBtn anBtn--send"
                                        onClick={handleSendToChasseurs}
                                        disabled={selectedChasseurs.length === 0 || sendingOfferToChasseurs}
                                        type="button"
                                    >
                                        <Send size={16} />
                                        {sendingOfferToChasseurs
                                            ? "Envoi..."
                                            : "Envoyer aux chasseurs sélectionnés"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 2 && (
                        <div className="anContent">
                            <div className="anCard">
                                <div className="anCardHeader">
                                    <h3 className="anCardTitle">
                                        Envoyer l'annonce aux partenaires académiques ({selectedPartenaires.length} sélectionné(s))
                                    </h3>
                                </div>

                                <div className="anTableWrap">
                                    <table className="anTable">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 50 }}>
                                                    <input type="checkbox" disabled />
                                                </th>
                                                <th>Nom du partenaire</th>
                                                <th>Type</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(partenairesAcademiques || []).map((partenaire) => {
                                                const isSelected = selectedPartenaires.includes(partenaire.id);
                                                return (
                                                    <tr key={partenaire.id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => togglePartenaire(partenaire.id)}
                                                            />
                                                        </td>
                                                        <td className="anStrong">{partenaire.nom}</td>
                                                        <td className="anMuted">{partenaire.email}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="anDateSection">
                                    <div className="anField">
                                        <label>Date limite de l'offre</label>
                                        <input
                                            type="date"
                                            value={dateLimiteOffre}
                                            onChange={(e) => setDateLimiteOffre(e.target.value)}
                                        />
                                    </div>

                                    <div className="anField">
                                        <label>Date d'envoi de l'email</label>
                                        <input
                                            type="date"
                                            value={dateEnvoiEmail}
                                            onChange={(e) => setDateEnvoiEmail(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="anTextRight">
                                    <button
                                        className="anBtn anBtn--send"
                                        onClick={handleSendToPartenaires}
                                        disabled={selectedPartenaires.length === 0 || sendingOfferToPartenaires}
                                        type="button"
                                    >
                                        <Send size={16} />
                                        {sendingOfferToPartenaires
                                            ? "Envoi..."
                                            : "Envoyer aux partenaires sélectionnés"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    loading: state.offresEmploi?.loading || false,
    saving: state.offresEmploi?.saving || false,
    error: state.offresEmploi?.error || null,
    generatedOffer: state.offresEmploi?.generatedOffer || null,
    chasseurs: state.chasseurs?.chasseurs || [],
    partenairesAcademiques: state.partenairesAcademiques?.partenairesAcademiques || [],
    sendingOfferToChasseurs: state.demandesRecrutement?.sendingOfferToChasseurs || false,
    sendingOfferToPartenaires: state.demandesRecrutement?.sendingOfferToPartenaires || false,
    sendOfferSuccess: state.demandesRecrutement?.sendOfferSuccess || "",
    sendOfferError: state.demandesRecrutement?.sendOfferError || null,
});

const mapDispatchToProps = (dispatch) => ({
    generateOffreEmploi: (payload) => dispatch(generateOffreEmploiRequest(payload)),
    saveOffreEmploiDraft: (id, payload) => dispatch(saveOffreEmploiDraftRequest(id, payload)),
    publishOffreEmploiCareers: (payload) => dispatch(publishOffreEmploiCareersRequest(payload)),
    clearGeneratedOffreEmploi: () => dispatch(clearGeneratedOffreEmploi()),
    fetchChasseurs: () => dispatch(fetchChasseursRequest()),
    fetchPartenaires: () => dispatch(fetchPartenairesAcadRequest()),
    sendOfferToChasseurs: (payload) => dispatch(sendOfferToChasseursRequest(payload)),
    sendOfferToPartenaires: (payload) => dispatch(sendOfferToPartenairesRequest(payload)),
    clearRecrutementEmailState: () => dispatch(clearRecrutementEmailState()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AnnonceModal);
